<template>
  <PublicInfoPageLayout
    eyebrow="Privacy"
    title="Star Group Privacy Policy"
    subtitle="This page explains what information Star Group processes for venue managers, staff members, and invited Friends of House users, including how account deletion and retention work."
  >
    <template #actions>
      <v-btn color="primary" variant="outlined" to="/support">
        Open support page
      </v-btn>
    </template>

    <div class="policy-stack">
      <section class="policy-section policy-meta">
        <div>
          <div class="meta-label">Effective date</div>
          <div class="meta-value">March 18, 2026</div>
        </div>
        <div>
          <div class="meta-label">Contact</div>
          <div class="meta-value">
            <a href="mailto:peter.bjerregaard@gmail.com">peter.bjerregaard@gmail.com</a>
          </div>
        </div>
      </section>

      <section class="policy-section">
        <h2>Who this policy applies to</h2>
        <p>
          This policy applies to users of the Star Group mobile app and related web services made available for venue managers,
          staff members, and invited Friends of House users of participating bars.
        </p>
      </section>

      <section class="policy-section">
        <h2>Information we may process</h2>
        <ul>
          <li>Account details such as name, email address, phone number, role, venue affiliation, profile information, and profile picture</li>
          <li>Operational app data such as handbook content, announcements, daily tasks, stock activity, wallet activity, discount redemption history, and other venue workflow records</li>
          <li>Content submitted by managers, staff members, invited Friends of House users, or the participating venue, including profile images and uploaded handbook or announcement media</li>
          <li>Technical and security data needed to operate the app, such as device identifiers, push notification tokens, session data, and audit or security logs</li>
        </ul>
      </section>

      <section class="policy-section">
        <h2>Why we use this information</h2>
        <ul>
          <li>To provide account access and venue-specific app features</li>
          <li>To support venue operations, staff communication, and VIP benefits</li>
          <li>To send important notifications and keep the service secure and reliable</li>
          <li>To respond to support requests and fix technical issues</li>
        </ul>
      </section>

      <section class="policy-section">
        <h2>How information may be shared</h2>
        <p>
          Information may be available to the participating bar or venue and to the venue managers responsible for managing your account.
          Information may also be
          processed by service providers used to operate the app, such as hosting, email, embedded media, and push notification providers.
          Personal information is not sold.
        </p>
      </section>

      <section class="policy-section">
        <h2>Data retention</h2>
        <ul>
          <li>Live account data is kept while your account is active and while the participating venue uses the service.</li>
          <li>If you delete your account, the live account, profile, login access, sessions, push devices, profile picture, current wallet balances, and other active account data are deleted.</li>
          <li>Business records needed to preserve venue history and reporting, such as stock records, daily task completion history, wallet ledger entries, discount redemption history, handbook content, and announcements, may be retained in anonymized form after account deletion.</li>
          <li>Wallet ledger entries and discount redemption history may temporarily retain identifying details linked to a deleted account for up to 180 days where necessary to investigate misuse, resolve disputes, correct operational mistakes, or protect the integrity of venue records. After that period, retained wallet and redemption history is anonymized.</li>
          <li>Forum or message content linked to a deleted account may be detached from the user account and, where appropriate, redacted.</li>
          <li>Audit and security logs may retain identifying information, including actor name, actor email, timestamps, request metadata such as IP address, and recorded changes, for up to 180 days after account deletion where necessary to investigate misuse, protect the integrity of the service, and handle security or compliance issues, before they are permanently deleted.</li>
          <li>Audit and security logs older than the retention period are deleted on a rolling basis.</li>
        </ul>
      </section>

      <section class="policy-section">
        <h2>Account deletion and your choices</h2>
        <ul>
          <li>You can request deletion of your account directly from the profile area in the app or web service.</li>
          <li>You can also contact support to request correction or deletion of personal information, subject to legal, security, or operational requirements.</li>
          <li>Managers and venue admins may still manage access for organization-managed accounts.</li>
          <li>You can manage push notifications in your iPhone settings.</li>
        </ul>
      </section>

      <section class="policy-section">
        <h2>Access controls</h2>
        <p>
          Access to personal information and retained audit logs is restricted to authorized personnel who need that access to operate,
          secure, or support the service for participating venues.
        </p>
      </section>

      <section class="policy-section">
        <h2>Contact</h2>
        <p>
          For privacy questions or requests, email <a href="mailto:peter.bjerregaard@gmail.com">peter.bjerregaard@gmail.com</a>.
        </p>
      </section>
    </div>
  </PublicInfoPageLayout>
</template>

<script setup>
import PublicInfoPageLayout from '@/components/PublicInfoPageLayout.vue';
</script>

<style scoped>
.policy-stack {
  display: grid;
  gap: 18px;
}

.policy-section {
  padding: 22px 24px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  background: rgba(248, 250, 252, 0.84);
}

.policy-section h2 {
  margin: 0 0 10px;
  color: #101828;
  font-size: 1.1rem;
}

.policy-section p,
.policy-section li {
  color: #475467;
  line-height: 1.7;
}

.policy-section ul {
  padding-left: 18px;
  margin: 0;
}

.policy-section a {
  color: #1d4ed8;
}

.policy-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.meta-label {
  color: #667085;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.meta-value {
  margin-top: 6px;
  color: #101828;
  font-size: 1rem;
  font-weight: 600;
}

@media (max-width: 600px) {
  .policy-section {
    padding: 18px;
  }

  .policy-meta {
    grid-template-columns: 1fr;
  }
}
</style>
